import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import PadelScoreBoard from './PadelScoreBoard';
import HandTracker from './HandTracker';
import DwellCursor from './DwellCursor';
import CelebrationOverlay from './CelebrationOverlay';
import SettingsModal from './SettingsModal';
import { synth } from '../../engine/audioSynthesizer';
import { getPlayerAvatar } from '../../utils/player';
import { isNativeApp } from '../../utils/platform';
import { isTeam1Serving } from './LiveSessionClassic';
import { calculateServer } from '../../utils/gameLogic';

// Helper for point-to-word translation (e.g. "Fifteen - Love")
const scoreToWord = (p) => {
  if (p === 0) return 'Love';
  if (p === 1) return 'Fifteen';
  if (p === 2) return 'Thirty';
  if (p === 3) return 'Forty';
  return 'Love';
};

export const isTeam1ServingMVP = isTeam1Serving;

export const getScorePhrase = (serverPoints, receiverPoints, serverNames = [], receiverNames = []) => {
  if (serverPoints >= 3 && receiverPoints >= 3) {
    if (serverPoints === receiverPoints) return 'Deuce';
    const sNames = (serverNames || []).map((p) => (typeof p === 'string' ? p : p?.name || '').trim().split(' ')[0]).filter(Boolean);
    const rNames = (receiverNames || []).map((p) => (typeof p === 'string' ? p : p?.name || '').trim().split(' ')[0]).filter(Boolean);
    if (serverPoints > receiverPoints) {
      return sNames.length ? `Advantage ${sNames.join(' and ')}` : 'Advantage';
    }
    if (receiverPoints > serverPoints) {
      return rNames.length ? `Advantage ${rNames.join(' and ')}` : 'Advantage';
    }
  }
  const w1 = scoreToWord(serverPoints);
  const w2 = scoreToWord(receiverPoints);
  if (w1.toLowerCase() === w2.toLowerCase()) return `${w1} All`;
  return `${w1} - ${w2}`;
};

export default function LiveSession({
  currentGame,
  nextGame,
  sessionPlayers = [],
  sessionHistory = [],
  gameRotation = [],
  currentSessionStats = {},
  currentServingPlayer,
  formatPointDisplay = (pts, oppPts) => {
    if (pts === 0) return '0';
    if (pts === 1) return '15';
    if (pts === 2) return '30';
    if (pts === 3 && oppPts < 4) return '40';
    if (pts >= 3 && pts === oppPts) return '40';
    if (pts > oppPts && pts >= 4) return 'A';
    if (oppPts > pts && oppPts >= 4) return '40';
    return '40';
  },
  goldenPoint: goldenPointProp = false,
  onToggleGoldenPoint: onToggleGoldenPointProp,
  allPlayers = [],
  db,
  selectedClubId,
  appId,
  clubName: clubNameProp,
  onPointScored,
  onTeamWin,
  onFinishSession = () => {},
  mvpLiveScore = [0, 0],
}) {
  const [team1Points, setTeam1Points] = useState(() => Number(mvpLiveScore?.[0]) || 0);
  const [team2Points, setTeam2Points] = useState(() => Number(mvpLiveScore?.[1]) || 0);
  const [pointHistory, setPointHistory] = useState([]);
  const [pairingCode, setPairingCode] = useState(null);
  const [isHandTrackingEnabled, setIsHandTrackingEnabledState] = useState(() => {
    if (!isNativeApp) return false; // hand tracking is native-app only
    try {
      const saved = localStorage.getItem('padelmvp_hand_tracking_enabled');
      return saved !== null ? JSON.parse(saved) : false; // off by default; opt in via Settings
    } catch (e) {
      return false;
    }
  });

  const setIsHandTrackingEnabled = (valOrFn) => {
    setIsHandTrackingEnabledState((prev) => {
      const nextVal = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        localStorage.setItem('padelmvp_hand_tracking_enabled', JSON.stringify(nextVal));
      } catch (e) {
        console.warn('Failed to save hand tracking preference:', e);
      }
      return nextVal;
    });
  };
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  const [localGoldenPoint, setLocalGoldenPoint] = useState(false);
  const goldenPoint = goldenPointProp !== undefined ? goldenPointProp : localGoldenPoint;

  const handleToggleGoldenPoint = () => {
    if (onToggleGoldenPointProp) {
      onToggleGoldenPointProp();
    } else {
      setLocalGoldenPoint((prev) => !prev);
    }
  };
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(() => {
    const saved = localStorage.getItem('padel_speech_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [celebrationData, setCelebrationData] = useState(null);
  const [cursorData, setCursorData] = useState(null);
  const [statusText, setStatusText] = useState('Initializing AI Vision...');

  useEffect(() => {
    synth.setSpeechEnabled(isSpeechEnabled);
  }, [isSpeechEnabled]);

  const handleToggleSpeech = () => {
    setIsSpeechEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('padel_speech_enabled', String(next));
      synth.setSpeechEnabled(next);
      return next;
    });
  };

  const fallbackTeam1 = sessionPlayers.length >= 2 ? [sessionPlayers[0]?.name || sessionPlayers[0], sessionPlayers[1]?.name || sessionPlayers[1]] : ['Team 1 A', 'Team 1 B'];
  const fallbackTeam2 = sessionPlayers.length >= 4 ? [sessionPlayers[2]?.name || sessionPlayers[2], sessionPlayers[3]?.name || sessionPlayers[3]] : ['Team 2 A', 'Team 2 B'];
  const team1Names = (currentGame?.team1 && currentGame.team1.length > 0) ? currentGame.team1 : fallbackTeam1;
  const team2Names = (currentGame?.team2 && currentGame.team2.length > 0) ? currentGame.team2 : fallbackTeam2;
  const sitsOutNames = currentGame?.sitsOut || '';
  const serverName = (typeof currentServingPlayer === 'string' ? currentServingPlayer : currentServingPlayer?.name) || team1Names[0] || 'Server';

  const isTeam1Serving = isTeam1ServingMVP(serverName, team1Names);

  const speakLiveScore = (t1, t2) => {
    const [servingPoints, receiverPoints] = isTeam1Serving ? [t1, t2] : [t2, t1];
    const [servingNames, receiverNames] = isTeam1Serving ? [team1Names, team2Names] : [team2Names, team1Names];
    const phrase = getScorePhrase(servingPoints, receiverPoints, servingNames, receiverNames);
    synth.speakScore(phrase);
  };
  const speakLiveScoreRef = useRef(speakLiveScore);
  speakLiveScoreRef.current = speakLiveScore;

  const wakeLockRef = useRef(null);
  const prevCurrentGameRef = useRef(undefined);
  const prevMvpLiveScoreRef = useRef([
    Number(mvpLiveScore?.[0]) || 0,
    Number(mvpLiveScore?.[1]) || 0,
  ]);

  // Screen Wake Lock
  useEffect(() => {
    let cancelled = false;

    const requestWakeLock = async () => {
      if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          await lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      } catch (e) {
        // Ignore wake lock errors
      }
    };

    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Ensure pairing code for smartwatch companion
  useEffect(() => {
    if (!db || !selectedClubId || !appId) return;
    const genCode = () => String(Math.floor(100000 + Math.random() * 900000));
    const sessionDocRef = doc(
      db,
      'artifacts',
      appId,
      'public',
      'data',
      'clubs',
      selectedClubId,
      'sessions',
      'current'
    );

    const ensurePairingCode = async () => {
      const clubName = typeof clubNameProp === 'string' ? clubNameProp : 'Club';
      try {
        const snap = await getDoc(sessionDocRef);
        const data = snap.exists() ? snap.data() : {};
        let code = data.pairingCode;
        if (!code) {
          code = genCode();
        }
        const expiresAt = Date.now() + 12 * 60 * 60 * 1000;
        const pairingCodesRef = doc(
          db,
          'artifacts',
          appId,
          'public',
          'data',
          'pairingCodes',
          code
        );

        setPairingCode(code);

        await setDoc(
          pairingCodesRef,
          { clubId: selectedClubId, clubName, expiresAt },
          { merge: true }
        );
      } catch (e) {
        console.error('Failed to ensure pairing code for MVP session:', e);
      }
    };

    ensurePairingCode();
  }, [db, selectedClubId, appId, clubNameProp]);

  const localActionTimestampRef = useRef(0);
  const prevSessionHistoryLenRef = useRef(-1);

  // Sync point state and announce scores when watch or remote device scores
  const propScore0 = Number(mvpLiveScore?.[0]) || 0;
  const propScore1 = Number(mvpLiveScore?.[1]) || 0;
  useEffect(() => {
    const prev = prevMvpLiveScoreRef.current;
    if (prev[0] !== propScore0 || prev[1] !== propScore1) {
      const isRemote = Date.now() - localActionTimestampRef.current > 1500;
      const isPointIncrease = (propScore0 > prev[0] && propScore1 === prev[1]) || (propScore1 > prev[1] && propScore0 === prev[0]);

      prevMvpLiveScoreRef.current = [propScore0, propScore1];
      setTeam1Points(propScore0);
      setTeam2Points(propScore1);

      if (isRemote && isPointIncrease && (propScore0 > 0 || propScore1 > 0)) {
        synth.init();
        const scoringTeam = propScore0 > prev[0] ? 1 : 2;
        synth.playPoint(scoringTeam === 1 ? 'A' : 'B');
        speakLiveScoreRef.current(propScore0, propScore1);
      }
    }
  }, [propScore0, propScore1]);

  // Game completion & Celebration overlay trigger when watch completes a game
  useEffect(() => {
    const currLen = sessionHistory ? sessionHistory.length : 0;
    const prevLen = prevSessionHistoryLenRef.current;
    prevSessionHistoryLenRef.current = currLen;

    if (prevLen === -1) return;

    if (currLen > prevLen) {
      const isRemote = Date.now() - localActionTimestampRef.current > 1500;
      if (isRemote) {
        const lastGame = sessionHistory[currLen - 1];
        if (lastGame) {
          synth.init();
          synth.playGame();

          const winningTeam = lastGame.winningTeam || [];
          const winnerNames = (winningTeam || [])
            .map((p) => (typeof p === 'string' ? p : p?.name || '').trim().split(' ')[0])
            .filter(Boolean)
            .join(' and ');
          synth.speakScore(`Game ${winnerNames || 'won'}`);

          const gr = (gameRotation && gameRotation.length > 0) ? gameRotation : [];
          const completedGameIndex = (currLen - 1) % (gr.length || 1);
          const completedGame = gr[completedGameIndex] || {};
          const nextGameIndex = currLen % (gr.length || 1);
          const upcomingGame = gr[nextGameIndex] || nextGame;
          // Rotations without a fixed server per game (3 players) work it out from the game number
          const upcomingNextGame = { ...upcomingGame, server: calculateServer(currLen, upcomingGame, sessionPlayers.length) };

          const team1Raw = completedGame.team1 || [];
          const team2Raw = completedGame.team2 || [];
          const team1Names = team1Raw.map((p) => (typeof p === 'string' ? p : p?.name));
          const isTeam1Winner = winningTeam.some((w) => {
            const wName = typeof w === 'string' ? w : w?.name;
            return team1Names.includes(wName);
          });
          const winnerIndex = isTeam1Winner ? 1 : 2;

          setCelebrationData({
            type: 'GAME',
            mode: 'mvp',
            team: winnerIndex,
            teamAName: team1Raw.map((p) => (typeof p === 'string' ? p : p?.name)).join(' & '),
            teamBName: team2Raw.map((p) => (typeof p === 'string' ? p : p?.name)).join(' & '),
            teamAAvatars: team1Raw.map((name) => getPlayerAvatar(allPlayers, typeof name === 'string' ? name : name?.name)),
            teamBAvatars: team2Raw.map((name) => getPlayerAvatar(allPlayers, typeof name === 'string' ? name : name?.name)),
            nextGame: upcomingNextGame,
          });
        }
      }
    }
  }, [sessionHistory, gameRotation, nextGame, allPlayers, sessionPlayers.length]);

  // Reset scores when moving to a new game
  const isRealGame = (g) => g && Array.isArray(g.team1) && g.team1.length > 0;
  const isSameGame = (a, b) => {
    if (!isRealGame(a) || !isRealGame(b)) return false;
    const t1A = (a.team1 || []).slice().sort().join(',');
    const t2A = (a.team2 || []).slice().sort().join(',');
    const t1B = (b.team1 || []).slice().sort().join(',');
    const t2B = (b.team2 || []).slice().sort().join(',');
    return t1A === t1B && t2A === t2B;
  };

  useEffect(() => {
    const isFirstRun = prevCurrentGameRef.current === undefined;
    if (isFirstRun) {
      prevCurrentGameRef.current = currentGame;
      return;
    }
    if (prevCurrentGameRef.current === currentGame) return;
    if (!isRealGame(prevCurrentGameRef.current) && isRealGame(currentGame)) {
      prevCurrentGameRef.current = currentGame;
      return;
    }
    if (isSameGame(prevCurrentGameRef.current, currentGame)) {
      prevCurrentGameRef.current = currentGame;
      return;
    }
    prevCurrentGameRef.current = currentGame;
    // Start from the live score rather than 0-0: it is 0-0 for a new game, but undoing a game win
    // moves back to the previous game with its score restored (and the sync effect above has already run)
    prevMvpLiveScoreRef.current = [propScore0, propScore1];
    setTeam1Points(propScore0);
    setTeam2Points(propScore1);
    setPointHistory([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame]);

  // Handle Point scoring
  const handleScorePoint = (teamIndex) => {
    localActionTimestampRef.current = Date.now();
    synth.init();

    const newT1 = teamIndex === 1 ? team1Points + 1 : team1Points;
    const newT2 = teamIndex === 2 ? team2Points + 1 : team2Points;

    // Check game win condition
    const isGameWon = (a, b) => {
      if (goldenPoint && a === 4 && b === 3) return true;
      if (goldenPoint && b === 4 && a === 3) return true;
      return (a >= 4 || b >= 4) && Math.abs(a - b) >= 2;
    };

    if (isGameWon(newT1, newT2)) {
      synth.playGame();
      const winner = newT1 > newT2 ? 1 : 2;
      const winningPlayers = winner === 1 ? currentGame?.team1 : currentGame?.team2;
      const winnerNames = (winningPlayers || []).map((p) => (typeof p === 'string' ? p : p?.name || '').trim().split(' ')[0]).filter(Boolean).join(' and ');
      synth.speakScore(`Game ${winnerNames || `Team ${winner}`}`);

      setCelebrationData({
        type: 'GAME',
        mode: 'mvp',
        team: winner,
        teamAName: (currentGame?.team1 || []).join(' & '),
        teamBName: (currentGame?.team2 || []).join(' & '),
        teamAAvatars: (currentGame?.team1 || []).map(name => getPlayerAvatar(allPlayers, typeof name === 'string' ? name : name?.name)),
        teamBAvatars: (currentGame?.team2 || []).map(name => getPlayerAvatar(allPlayers, typeof name === 'string' ? name : name?.name)),
        nextGame: { ...nextGame, server: calculateServer((sessionHistory?.length || 0) + 1, nextGame, sessionPlayers.length) },
      });

      prevMvpLiveScoreRef.current = [0, 0];
      setTeam1Points(0);
      setTeam2Points(0);
      setPointHistory([]);

      if (onPointScored) {
        onPointScored(0, 0);
      }
      if (onTeamWin) {
        onTeamWin(winner, {
          team1: newT1,
          team2: newT2,
          winningTeam: winner === 1 ? newT1 : newT2,
          losingTeam: winner === 1 ? newT2 : newT1,
        });
      }
    } else {
      synth.playPoint(teamIndex === 1 ? 'A' : 'B');
      speakLiveScore(newT1, newT2);

      setTeam1Points(newT1);
      setTeam2Points(newT2);
      setPointHistory((prev) => [...prev, [team1Points, team2Points]]);

      if (onPointScored) {
        onPointScored(newT1, newT2);
      }
    }
  };

  const handleUndo = async () => {
    localActionTimestampRef.current = Date.now();
    synth.init();
    if (team1Points === 0 && team2Points === 0) {
      if (!sessionHistory || sessionHistory.length === 0) return;
      synth.playUndo();
      const lastGame = sessionHistory[sessionHistory.length - 1];
      const newHist = sessionHistory.slice(0, -1);
      const prevIndex = newHist.length % (gameRotation?.length || 1);
      const prevGame = gameRotation?.[prevIndex] || {};
      const winners = lastGame.winningTeam || [];
      const team1Names = prevGame.team1 || [];

      const getFirstName = (p) => (typeof p === 'string' ? p : p?.name || '').trim();
      const isWinnerTeam1 = Array.isArray(winners) && winners.length > 0 && winners.every(w => team1Names.map(getFirstName).indexOf(getFirstName(w)) >= 0);
      const finalW = Math.max(1, (lastGame.scores && lastGame.scores.winningTeam) || 4);
      const finalL = Math.max(0, (lastGame.scores && lastGame.scores.losingTeam) || 0);

      const restoredT1 = isWinnerTeam1 ? Math.max(0, finalW - 1) : finalL;
      const restoredT2 = isWinnerTeam1 ? finalL : Math.max(0, finalW - 1);

      if (db && selectedClubId) {
        const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
        try {
          await updateDoc(sessionDocRef, {
            sessionHistory: newHist,
            mvpLiveScore: [restoredT1, restoredT2]
          });
        } catch (e) {
          console.error('Failed to undo MVP game:', e);
        }
      }
      return;
    }
    synth.playUndo();
    let restoredT1 = 0;
    let restoredT2 = 0;
    if (pointHistory.length > 0) {
      const prev = pointHistory[pointHistory.length - 1];
      setPointHistory((history) => history.slice(0, -1));
      restoredT1 = Array.isArray(prev) ? prev[0] : 0;
      restoredT2 = Array.isArray(prev) ? prev[1] : 0;
    } else {
      const teamToUndo = team1Points >= team2Points ? 1 : 2;
      restoredT1 = teamToUndo === 1 ? Math.max(0, team1Points - 1) : team1Points;
      restoredT2 = teamToUndo === 2 ? Math.max(0, team2Points - 1) : team2Points;
    }
    prevMvpLiveScoreRef.current = [restoredT1, restoredT2];
    setTeam1Points(restoredT1);
    setTeam2Points(restoredT2);

    if (db && selectedClubId) {
      const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
      try {
        await updateDoc(sessionDocRef, { mvpLiveScore: [restoredT1, restoredT2] });
      } catch (e) {}
    }

    speakLiveScore(restoredT1, restoredT2);

    if (onPointScored) {
      onPointScored(restoredT1, restoredT2);
    }
  };

  const handleScorePointRef = useRef(null);
  const handleUndoRef = useRef(null);
  handleScorePointRef.current = handleScorePoint;
  handleUndoRef.current = handleUndo;

  const handleDwellTrigger = useCallback((zoneId) => {
    console.log('🎯 HandTracker Dwell Triggered:', zoneId);
    if (celebrationData) return;

    if (zoneId === 'TEAM_A_HALF') {
      handleScorePointRef.current?.(1);
    } else if (zoneId === 'TEAM_B_HALF') {
      handleScorePointRef.current?.(2);
    } else if (zoneId === 'UNDO') {
      handleUndoRef.current?.();
    } else if (zoneId === 'SETTINGS') {
      setIsSettingsOpen(true);
      synth.playPoint('A');
    } else if (zoneId === 'FINISH_SESSION') {
      onFinishSession();
    }
  }, [celebrationData, onFinishSession]);

  return (
    <div className="relative w-screen h-screen bg-transparent text-slate-100 overflow-hidden flex flex-col font-sans select-none">
      
      {/* MediaPipe Hand Tracker Engine */}
      <HandTracker
        onCursorMove={setCursorData}
        onDwellTrigger={handleDwellTrigger}
        showCameraPreview={showCameraPreview}
        onCloseCameraPreview={() => setShowCameraPreview(false)}
        isHandTrackingEnabled={isHandTrackingEnabled}
        onStatusChange={setStatusText}
      />

      {/* Dwell Cursor Overlay */}
      {!celebrationData && isHandTrackingEnabled && (
        <DwellCursor cursorData={cursorData} />
      )}

      {/* Celebration Overlay */}
      <CelebrationOverlay
        eventData={celebrationData}
        onDismiss={() => setCelebrationData(null)}
        allPlayers={allPlayers}
      />

      {/* Scoreboard UI */}
      <main className="flex-1 w-full h-full">
        <PadelScoreBoard
          mode="mvp"
          team1Players={team1Names}
          team2Players={team2Names}
          servingPlayer={serverName}
          team1ScoreDisplay={formatPointDisplay(team1Points, team2Points)}
          team2ScoreDisplay={formatPointDisplay(team2Points, team1Points)}
          allPlayers={allPlayers}
          onPointScore={(team) => handleScorePoint(team)}
          onUndo={handleUndo}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onFinishSession={onFinishSession}
          pairingCode={pairingCode}
          hoverZone={cursorData?.currentZone}
          celebrationData={celebrationData}
          nextGame={nextGame}
          sitsOutNames={sitsOutNames}
          sessionPlayers={sessionPlayers}
          currentSessionStats={currentSessionStats}
        />
      </main>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        isHandTrackingEnabled={isHandTrackingEnabled}
        setIsHandTrackingEnabled={setIsHandTrackingEnabled}
        showCameraPreview={showCameraPreview}
        setShowCameraPreview={setShowCameraPreview}
        goldenPoint={goldenPoint}
        onToggleGoldenPoint={handleToggleGoldenPoint}
        isSpeechEnabled={isSpeechEnabled}
        onToggleSpeech={handleToggleSpeech}
      />

    </div>
  );
}
